import { useCallback, useEffect, useRef, useState } from 'react';
import { ApolloError } from 'apollo-client';
import {
	useEpicsStoreActions,
	useHasFullFetchStarted,
} from '@atlassian/jira-backlog-epics-store/src/selectors/index.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import {
	useAnalyticsEvents,
	fireOperationalAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { isHttpClientErrorResponse } from '@atlassian/jira-software-logging/src/common/utils.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { PAGE_SIZE } from '../../common/constants.tsx';
import { usePanelData } from '../../context/use-panel-data.tsx';
import type { FullEpicDetails_boardScope_parents as IssueParentsFull } from '../../services/epic/get/__generated_apollo__/FullEpicDetails.ts';
import { useEpicDetailsQuery } from '../../services/epic/get/index.tsx';
import { transformFullCard } from '../../services/epic/get/utils.tsx';
import {
	BACKLOG_EPIC_PANEL_FIRST_CHUNK_END_MARK,
	epicPanelPaginationMetric,
} from '../../services/performance/metrics-custom-epic-panel-load/index.tsx';
import { mapEpicsCountToBucket } from './utils.tsx';

type OnFetchCallback = (data: IssueParentsFull | null, startIndex: number) => void;

type PaginatedEpicsParams = {
	boardId: number;
	isCompanyManaged: boolean;
};

const UNMOUNTED_FETCH_ID = -1;

// VISIBLE FOR TESTING
export const usePaginatedEpics = ({ boardId, isCompanyManaged }: PaginatedEpicsParams) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsEvent = createAnalyticsEvent({});

	const { refetch } = useEpicDetailsQuery({ boardId, isCompanyManaged });
	const fetchIdRef = useRef(0);

	const [, { updateEpicsAttributes, updateQueryState }] = useEpicsStoreActions();

	const fetchPage = useCallback(
		async ({
			onFetch,
			fetchId = fetchIdRef.current,
			startCursor,
			count = 0,
		}: {
			onFetch: OnFetchCallback;
			fetchId: number;
			startCursor?: string;
			count?: number;
		}) => {
			if (fetchId !== fetchIdRef.current) {
				fireOperationalAnalytics(analyticsEvent, 'epicPanelRefetch aborted', {
					epicsCount: count,
				});
				return;
			}

			if (!startCursor) {
				epicPanelPaginationMetric.start();
			}

			try {
				const { data, errors } = await refetch({
					boardId: String(boardId),
					isCompanyManaged,
					pageSize: PAGE_SIZE,
					startCursor,
				});

				if (errors?.length) {
					throw new ApolloError({ graphQLErrors: errors });
				}

				const parents = data?.boardScope?.parents;

				const newCount = count + (parents?.edges?.length ?? 0);
				const hasNextPage = parents?.pageInfo?.hasNextPage ?? false;
				const endCursor = parents?.pageInfo?.endCursor;

				if (!startCursor) {
					epicPanelPaginationMetric.mark(BACKLOG_EPIC_PANEL_FIRST_CHUNK_END_MARK);
				}

				if (!hasNextPage) {
					epicPanelPaginationMetric.stop({
						customData: {
							isCompanyManaged,
							epicsCount: newCount,
						},
					});
					fireOperationalAnalytics(analyticsEvent, 'epicPanelRefetch succeeded', {
						// Kept for investigation purposes in Splunk
						epicsCount: newCount,
					});
					// Separate event to avoid high cardinality
					fireOperationalAnalytics(analyticsEvent, 'epicPanelRefetch bucket', {
						bucket: mapEpicsCountToBucket(newCount),
					});
				}

				onFetch(parents, count);

				hasNextPage &&
					endCursor &&
					fetchPage({ onFetch, fetchId, startCursor: endCursor, count: newCount });
			} catch (error) {
				if (!(error instanceof Error) || fetchIdRef.current === UNMOUNTED_FETCH_ID) {
					/**
					 * there is a case where refetch still runs after the epic panel is unmounted which
					 * throws an error, but we ignore this case.
					 * https://github.com/apollographql/react-apollo/issues/3862
					 *
					 * We still throw any other errors for the error boundary to catch.
					 */
					return;
				}

				// erroring epic cards in the panel that have yet to be loaded
				updateEpicsAttributes({ isLoading: false, hasError: true }, count);

				epicPanelPaginationMetric.cancel();
				fireOperationalAnalytics(analyticsEvent, 'epicPanelRefetch failed', {
					epicsCount: count,
					error,
					isClientFetchError: isClientFetchError(error) || isHttpClientErrorResponse(error),
				});

				if (error instanceof ApolloError) {
					updateQueryState({ refetchError: error });
				} else {
					throw error;
				}
			}
		},
		[analyticsEvent, boardId, isCompanyManaged, refetch, updateEpicsAttributes, updateQueryState],
	);

	useEffect(
		() => () => {
			fetchIdRef.current = UNMOUNTED_FETCH_ID;
		},
		[],
	);

	return {
		fetch: ({ onFetch }: { onFetch: OnFetchCallback }) =>
			fetchPage({ onFetch, fetchId: ++fetchIdRef.current }),
	};
};

export const useEpicsRefetch = () => {
	const [
		hasFullFetchStarted,
		{ updateQueryState, updateEpics, updateEpicsAttributes, updateHasFullFetchStarted },
	] = useHasFullFetchStarted();

	const [hasStartedFullFetchOld, setHasStartedFullFetchOld] = useState(false);

	const { boardId, isCompanyManaged } = usePanelData();
	const { fetch } = usePaginatedEpics({ boardId, isCompanyManaged });

	const updateEpicsStore = useCallback(
		(parents: IssueParentsFull | null, startIndex: number) => {
			const updatedCards =
				parents?.edges?.flatMap((edge) => (edge?.node ? transformFullCard(edge.node) : [])) ?? [];

			updateEpics({
				updatedCards,
				startIndex,
				hasNextPage: parents?.pageInfo.hasNextPage ?? false,
			});
		},
		[updateEpics],
	);

	const fetchData = useCallback(
		({ triggerLoadingState = false } = {}) => {
			if (fg('prefetch-epics-data-backlog')) {
				updateHasFullFetchStarted(true);
			} else {
				setHasStartedFullFetchOld(true);
			}

			updateQueryState({ error: undefined, refetchError: undefined });

			triggerLoadingState && updateEpicsAttributes({ isLoading: true });
			fetch({ onFetch: updateEpicsStore });
		},
		[updateQueryState, updateEpicsAttributes, fetch, updateEpicsStore, updateHasFullFetchStarted],
	);

	return {
		refetch: fetchData,
		hasFullFetched: fg('prefetch-epics-data-backlog')
			? hasFullFetchStarted
			: hasStartedFullFetchOld,
	};
};

export const useEpicsPrefetch = ({ boardId, isCompanyManaged }: PaginatedEpicsParams) => {
	const { fetch } = usePaginatedEpics({ boardId, isCompanyManaged });
	const [hasFullFetchStarted, { updateQueryState, updateEpics, updateHasFullFetchStarted }] =
		useHasFullFetchStarted();

	const updateEpicsStore = useCallback(
		(parents: IssueParentsFull | null, startIndex: number) => {
			const updatedCards =
				parents?.edges?.flatMap((edge) => (edge?.node ? transformFullCard(edge.node) : [])) ?? [];

			updateEpics({
				updatedCards,
				startIndex,
				hasNextPage: parents?.pageInfo.hasNextPage ?? false,
			});

			updateQueryState({ loading: false });
		},
		[updateEpics, updateQueryState],
	);

	const prefetchData = useCallback(() => {
		updateHasFullFetchStarted(true);
		updateQueryState({ loading: true, error: undefined, refetchError: undefined });
		fetch({ onFetch: updateEpicsStore });
	}, [updateQueryState, fetch, updateEpicsStore, updateHasFullFetchStarted]);

	return {
		hasFullFetchStarted,
		prefetch: prefetchData,
	};
};
